/**
 * Shared visual language + plumbing for the v2 detail dialogs (the design's
 * Send-Link dialog anatomy, §12): white radius-24 surface, display-serif
 * title, gray subtitle, floating-label fields, dark pill CTA. Also the
 * fetcher→toast bridge: per the project's no-useEffect rule, completed
 * action results are handled in a REF CALLBACK on a marker element that
 * renders exactly once per unhandled result (v1 used useEffect-on-data).
 */
import { XIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useRef, useState } from 'react'
import { toast } from 'sonner'
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '#app/components/ui/dialog'
import type { DetailActionResult } from '#app/utils/application-detail-v2/types'
import { cn } from '#app/utils/misc'

/** Minimal slice of a react-router fetcher these helpers consume. */
export interface DetailActionFetcherLike {
	state: 'idle' | 'loading' | 'submitting'
	data?: DetailActionResult
}

/**
 * Fires success/error toasts (v1 parity: toast.success(message || fallback)
 * / toast.error(error), v1:4510-4546) and closes the dialog on success.
 * Renders only while a completed result is unhandled; the ref callback runs
 * post-commit, marks the result handled, and disappears on the next render.
 */
export function ActionResultMarker({
	fetcher,
	fallback,
	onSuccess,
}: {
	fetcher: DetailActionFetcherLike
	fallback: string
	onSuccess?: () => void
}) {
	const lastHandled = useRef<DetailActionResult | null>(null)
	const result = fetcher.state === 'idle' ? fetcher.data : undefined
	if (!result || lastHandled.current === result) return null
	return (
		<span
			hidden
			ref={() => {
				// Also runs on detach (ref(null)) — the guard makes that a no-op.
				if (lastHandled.current === result) return
				lastHandled.current = result
				if (result.ok) {
					// Server-side warnings ride the ok result (early decline's "N of
					// M not finished", removal's "regenerate the family report"
					// nudge) — surface them as the toast's description so message +
					// warning stay in ONE box. Firing a separate toast.warning stacked
					// into a "double box" behind the success toast (fixed 2026-07-16).
					toast.success(result.message || fallback, {
						description: result.warning ?? undefined,
					})
					onSuccess?.()
				} else {
					toast.error(result.error)
				}
			}}
		/>
	)
}

/**
 * Inline failure line inside dialogs (v1 keeps it alongside the toast).
 * Fetcher data outlives the dialog, so whatever result existed when the
 * dialog (re)opened is marked stale and never rendered — only an error from
 * a submit made during THIS open shows (v1 cleared error state on open,
 * v1:7953-7959). Prop-change tracking uses the documented render-time
 * setState pattern (no useEffect, project rule).
 */
export function InlineActionError({
	fetcher,
	open,
}: {
	fetcher: DetailActionFetcherLike
	open: boolean
}) {
	const [staleResult, setStaleResult] = useState(fetcher.data)
	const [wasOpen, setWasOpen] = useState(open)
	if (open !== wasOpen) {
		setWasOpen(open)
		if (open) setStaleResult(fetcher.data)
	}
	const result = fetcher.state === 'idle' ? fetcher.data : undefined
	if (!result || result.ok || result === staleResult) return null
	return <p className="text-sm text-red-600">{result.error}</p>
}

/** Dialog shell in the Send-Link visual language (design §12). */
export function DetailDialog({
	open,
	onOpenChange,
	title,
	description,
	className,
	children,
	button,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	title: string
	description?: React.ReactNode
	className?: string
	children: ReactNode
	button?: ReactNode
}) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				// Fade only — no zoom. The shared DialogContent opens with
				// `zoom-in-95`, whose keyframe scales the dialog via scale3d(); the
				// browser rasterises the whole dialog once and resamples that texture
				// each frame. FloatingField's label hides the field's 1px border by
				// painting an opaque patch over it, and resampling makes that hairline
				// flash through the notch for the ~200ms the animation runs. Killing
				// the scale (the fade is untouched) removes the resample, so there is
				// nothing to flash. Set inline because the enter/exit scale are CSS
				// variables the utility writes — a competing class wouldn't reliably win.
				style={
					{ '--tw-enter-scale': 1, '--tw-exit-scale': 1 } as React.CSSProperties
				}
				// Hide the stock absolute close — Figma aligns the × with the title
				// row / content padding, not the tighter top-4/right-4 inset.
				showCloseButton={false}
				className={cn(
					'gap-6 rounded-3xl border-[#ECECEA] bg-white p-8 text-left sm:max-w-xl sm:p-8',
					className,
				)}
			>
				<DialogHeader className="shrink-0 gap-1.5 text-left">
					<div className="flex items-start justify-between gap-4">
						<DialogTitle className="font-calgary pr-2 text-[28px] leading-tight font-medium text-[#1F1E1D]">
							{title}
						</DialogTitle>
						<div className="flex shrink-0 items-center gap-2">
							{button}
							<DialogClose
								className="cursor-pointer rounded-xs text-[#747474] opacity-70 transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden focus:outline-hidden disabled:pointer-events-none [&_svg]:size-4"
								aria-label="Close"
							>
								<XIcon />
							</DialogClose>
						</div>
					</div>
					{description ? (
						<DialogDescription className="text-left text-[15px] leading-snug text-[#747474]">
							{description}
						</DialogDescription>
					) : null}
				</DialogHeader>
				{children}
			</DialogContent>
		</Dialog>
	)
}

/** Material-outlined box with the label floating ON the border (design §12). */
export function FloatingField({
	label,
	htmlFor,
	className,
	error,
	children,
}: {
	label: string
	htmlFor?: string
	className?: string
	/** Validation message — when set, the field shows a red border and the
	 * message renders below it. */
	error?: string
	children: ReactNode
}) {
	return (
		<div className={className}>
			<div
				className={cn(
					'relative rounded-xl border px-4 py-3 focus-within:border-[#32302F]',
					error
						? 'border-[#DC2634] focus-within:border-[#DC2634]'
						: 'border-[#F0F0F0]',
				)}
			>
				<label
					htmlFor={htmlFor}
					className="absolute -top-2.5 left-3 bg-[#fbfdfd] px-1.5 text-[13px] text-[#32302F]"
				>
					{label}
				</label>
				{children}
			</div>
			{error ? (
				<p className="mt-1.5 px-1 text-[13px] text-[#DC2634]">{error}</p>
			) : null}
		</div>
	)
}

/**
 * Shared cancel-invitation confirm (QA 2026-07-17/18): cancelling is
 * irreversible — the invitee can never join, their link dies, and a
 * cancelled invite cannot be resent (D3) — so NO surface may cancel on a
 * single click. Used by the combined tiles / guarantor popover
 * (CancelInviteButton) and the All Applicants side panel.
 */
export function CancelInviteConfirm({
	open,
	onOpenChange,
	invitedLabel,
	busy,
	onConfirm,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	invitedLabel: string
	busy: boolean
	onConfirm: () => void
}) {
	return (
		<DetailDialog
			open={open}
			onOpenChange={onOpenChange}
			title="Cancel this invitation?"
			description={`${invitedLabel} will no longer be able to join this application.`}
			className="sm:max-w-md"
		>
			<div className="flex justify-end gap-3">
				<PillButton
					tone="outline"
					disabled={busy}
					onClick={() => onOpenChange(false)}
				>
					Keep invitation
				</PillButton>
				<PillButton tone="danger" disabled={busy} onClick={onConfirm}>
					{busy ? 'Cancelling...' : 'Cancel invitation'}
				</PillButton>
			</div>
		</DetailDialog>
	)
}

export type PillButtonTone = 'dark' | 'success' | 'danger' | 'outline'

const PILL_TONES: Record<PillButtonTone, string> = {
	dark: 'bg-[#32302F] text-white hover:bg-[#1a1918]',
	success: 'bg-green-600 text-white hover:bg-green-700',
	danger: 'bg-[#DC2634] text-white hover:bg-red-700',
	outline: 'border border-[#C9C9C7] bg-white text-[#32302F] hover:bg-[#F6F7F5]',
}

/** Fully-rounded pill button (the design's dark-ink CTA + outline mate). */
export function PillButton({
	tone = 'dark',
	className,
	type = 'button',
	...props
}: React.ComponentProps<'button'> & { tone?: PillButtonTone }) {
	return (
		<button
			type={type}
			className={cn(
				'flex h-10 cursor-pointer items-center justify-center gap-2 rounded-full px-6 text-[15px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60',
				PILL_TONES[tone],
				className,
			)}
			{...props}
		/>
	)
}
